import { appConfig } from "@/types/appConfig.type.ts";

const OBFUSCATION_VERSION = 1;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

type ObfuscationEnvelope = {
  v: number;
  data: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  let base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4 !== 0) {
    base64 += "=";
  }

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function xorBase64Encode(value: string, key: string): string {
  if (!key) throw new Error("Missing VITE_API_OBFUSCATION_KEY");

  const valueBytes = textEncoder.encode(value);
  const keyBytes = textEncoder.encode(key);
  const xored = new Uint8Array(valueBytes.length);

  for (let index = 0; index < valueBytes.length; index += 1) {
    const valueByte = valueBytes[index] ?? 0;
    const keyByte = keyBytes[index % keyBytes.length] ?? 0;
    xored[index] = valueByte ^ keyByte;
  }

  return bytesToBase64Url(xored);
}

function xorBase64Decode(value: string, key: string): string {
  if (!key) throw new Error("Missing VITE_API_OBFUSCATION_KEY");

  const encryptedBytes = base64UrlToBytes(value);
  const keyBytes = textEncoder.encode(key);
  const decoded = new Uint8Array(encryptedBytes.length);

  for (let index = 0; index < encryptedBytes.length; index += 1) {
    const encryptedByte = encryptedBytes[index] ?? 0;
    const keyByte = keyBytes[index % keyBytes.length] ?? 0;
    decoded[index] = encryptedByte ^ keyByte;
  }

  return textDecoder.decode(decoded);
}

export function isObfuscationEnabled(): boolean {
  return (
    appConfig.VITE_API_OBFUSCATION_ENABLED === "true" &&
    appConfig.VITE_API_OBFUSCATION_KEY.length > 0
  );
}

export function getObfuscationVersion(): string {
  return String(OBFUSCATION_VERSION);
}

export function isObfuscationEnvelope(
  payload: unknown,
): payload is ObfuscationEnvelope {
  if (!isRecord(payload)) return false;

  const keys = Object.keys(payload);
  return (
    keys.length === 2 &&
    payload.v === OBFUSCATION_VERSION &&
    typeof payload.data === "string"
  );
}

export function obfuscatePayload(payload: unknown): ObfuscationEnvelope {
  return {
    v: OBFUSCATION_VERSION,
    data: xorBase64Encode(
      JSON.stringify(payload),
      appConfig.VITE_API_OBFUSCATION_KEY,
    ),
  };
}

export function deobfuscatePayload(payload: unknown): unknown {
  if (!isObfuscationEnvelope(payload)) {
    throw new Error("Invalid obfuscation envelope");
  }

  return JSON.parse(
    xorBase64Decode(payload.data, appConfig.VITE_API_OBFUSCATION_KEY),
  ) as unknown;
}
